<template>
  <form class="space-y-4" @submit="handleSaveInsuranceCase">
    <div class="grid gap-4 md:grid-cols-3">
      <SelectField
        class="basis-1/3"
        :name="`insuranceProvider`"
        :label="t('insuranceProvider')"
        :options="insuranceProviderOptions"
      />
      <LabeledText v-if="initialInsuranceCase?.status" :label="t('status')">
        {{ t('insuranceCaseStatus.' + initialInsuranceCase?.status) }}
      </LabeledText>
      <CVButton
        v-if="initialInsuranceCase && !initialInsuranceCase.booking"
        @click.prevent="createBooking"
        >{{ t('bookInsuranceCase') }}</CVButton
      >
      <CVButton
        v-else-if="initialInsuranceCase?.booking"
        :to="{ name: 'bookingEdit', params: { id: initialInsuranceCase.booking.id } }"
        outline
      >
        {{ t('viewLinkedBooking') }}
      </CVButton>
    </div>

    <Divider />

    <div v-if="formValues.insuranceProvider">
      <AxaInsuranceDataForm v-if="formValues.insuranceProvider === 'axa'" />

      <RolandInsuranceDataForm v-else-if="formValues.insuranceProvider === 'roland'" />

      <EuropInsuranceDataForm v-else-if="formValues.insuranceProvider === 'europ'" />

      <VolvoInsuranceDataForm v-else-if="formValues.insuranceProvider === 'volvo'" />

      <JlrInsuranceDataForm v-else-if="formValues.insuranceProvider === 'jlr'" />

      <Divider />
    </div>

    <div class="space-y-4">
      <div class="grid gap-4 md:grid-cols-3">
        <TextareaField name="additionalInfo" :label="t('additionalInfo')" class="col-span-2" />
      </div>
    </div>

    <Divider />

    <div class="space-y-4">
      <h2>{{ t('timeAndLocation') }}</h2>
      <div class="grid gap-4 md:grid-cols-3">
        <BookingRangePicker
          :start-date="formValues.startDate!"
          :end-date="formValues.endDate!"
          class="md:col-span-2"
          :start-date-title="t('startDate')"
          :end-date-title="t('endDate')"
          @change="handleTimeRangeChange"
        />
        <div class="col-start-1" data-testid="location">
          <InputLabel :label="t('pickupLocation')" />
          <SearchLocation
            :location="convertToGeocodedLocation(formValues.locationGeocodedAddress)"
            @update:location="handleLocationChange($event)"
          />
        </div>
      </div>
    </div>

    <Divider />

    <div class="space-y-4">
      <h2>{{ t('customerData') }}</h2>
      <CustomerDataForm name="customerData" hide-email hide-header />
      <div v-if="!initialInsuranceCase" class="grid gap-4 md:grid-cols-3">
        <InputField name="email" :label="t('email')" />
      </div>
    </div>

    <Divider />

    <div v-if="initialInsuranceCase" class="space-y-4">
      <h2>{{ t('linkedCustomerAccount') }}</h2>

      <div v-if="!isSwappingCustomer" class="grid gap-2 md:flex md:items-center md:justify-between">
        <LabeledText :label="t('customerAccount')">
          {{ initialInsuranceCase?.customer?.email ?? t('noCustomerAccount') }}
        </LabeledText>
        <div class="grid gap-2 md:flex">
          <CVButton outline :disabled="isSaving" @click.prevent="isSwappingCustomer = true">{{
            initialInsuranceCase.customer?.id ? t('swapAccount') : t('linkAccount')
          }}</CVButton>
          <CVButton
            v-if="$can('manage', 'User') && initialInsuranceCase.customer"
            outline
            :disabled="isSaving"
            @click.prevent="viewAccount"
            >{{ t('viewAccount') }}</CVButton
          >
        </div>
      </div>

      <SwapCustomer
        v-if="isSwappingCustomer"
        :schema="userEditSchema"
        :initial-customer-data="initialInsuranceCase?.customerData"
        :swap-button-text="initialInsuranceCase?.customer ? t('swap') : t('add')"
        :is-saving="isSaving"
        @swap-customer="swapCustomer"
        @cancel="isSwappingCustomer = false"
      />

      <Divider />
    </div>

    <div class="flex justify-between">
      <CVButton
        v-if="initialInsuranceCase?.status === InsuranceCaseStatus.INITIAL"
        :disabled="isSaving"
        :is-loading="isDeleting"
        variant="danger"
        @click.prevent="emit('delete')"
        >{{ t('delete') }}</CVButton
      >
      <div class="flex flex-1 justify-end">
        <CVButton variant="default" :is-loading="isSaving">{{ t('save') }}</CVButton>
      </div>
    </div>
  </form>
</template>

<script setup lang="ts">
import { useForm } from 'vee-validate';
import CustomerDataForm from '@/components/booking-forms/customer/CustomerDataForm.vue';
import InputField from '@/components/InputField.vue';
import { useI18n } from 'vue-i18n';
import Divider from '@/components/Divider.vue';
import BookingRangePicker from '@/views/handover/components/overview/BookingRangePicker.vue';
import { DateTime } from 'luxon';
import SelectField from '@/components/SelectField.vue';
import { computed, ref } from 'vue';
import { createInsuranceCaseSchema } from '@/validation/insurance-case.schema';
import AxaInsuranceDataForm from './AxaInsuranceDataForm.vue';
import {
  type CreateInsuranceCaseDto,
  type InsuranceCase,
  InsuranceCaseStatus,
  Local,
  type LocationDto,
  type LocationGeocodedAddress,
  type UpdateInsuranceCaseDto,
} from '@carvia/ros-client-types';
import SearchLocation from '@/components/booking-forms/SearchLocation.vue';
import type { ExtendedGeocodedLocation } from '@/components/GooglePlacesInput.vue';
import InputLabel from '@/components/InputLabel.vue';
import { cloneDeep } from 'lodash';
import { usePreventLeavingUnsavedForm } from '@/hooks/use-prevent-leaving-unsaved-form';
import { formHasChanged } from '@/hooks/use-form-has-changed';
import SwapCustomer from '@/components/booking-forms/customer/SwapCustomer.vue';
import { type User } from '@/entities/auth/user.entity';
import router from '@/router';
import { userEditSchema } from '@/validation/user.schema';
import { Alert } from '@/services/alert.service';
import LabeledText from '@/components/LabeledText.vue';
import TextareaField from '@/components/TextareaField.vue';
import type { LocationGeocodedName } from '@/entities/bookings/booking.entity';
import RolandInsuranceDataForm from '@/views/insurance-cases/insurance-case/RolandInsuranceDataForm.vue';
import EuropInsuranceDataForm from './EuropInsuranceDataForm.vue';
import VolvoInsuranceDataForm from './VolvoInsuranceDataForm.vue';
import { useInsuranceProviderOptions } from './use-insurance-provider-options';
import JlrInsuranceDataForm from './JlrInsuranceDataForm.vue';

const props = defineProps<{
  initialInsuranceCase?: InsuranceCase;
  isSaving?: boolean;
  isDeleting?: boolean;
}>();

const emit = defineEmits<{
  (e: 'submit', value: CreateInsuranceCaseDto | UpdateInsuranceCaseDto): void;
  (e: 'delete'): void;
}>();

const { t } = useI18n();
const isSwappingCustomer = ref(false);

const initialStartDate = DateTime.now()
  .plus({ days: 1 })
  .set({ hour: 11, minute: 0, second: 0, millisecond: 0 });

type InsuranceCaseLocationGeocodedAddress = LocationGeocodedAddress &
  Partial<Pick<LocationGeocodedName, 'street' | 'city' | 'zip' | 'country'>>;

const convertToGeocodedLocation = (
  location: InsuranceCaseLocationGeocodedAddress | undefined | null,
) => {
  if (!location) return undefined;
  return {
    address: {
      displayName: location.displayName,
      street: location.street,
      city: location.city,
      zip: location.zip,
      country: location.country,
    },
    coordinates: location.location,
  };
};

const handleLocationChange = (location: ExtendedGeocodedLocation | null) => {
  const locationGeocodedAddress: InsuranceCaseLocationGeocodedAddress | null = location
    ? {
        location: location.coordinates as LocationDto,
        displayName: location.address?.displayName ?? '',
        street: location.address?.street,
        city: location.address?.city,
        zip: location.address?.zip,
        country: location.address?.country,
      }
    : location;
  setFieldValue('locationGeocodedAddress', locationGeocodedAddress as LocationGeocodedAddress);
};

const swapCustomer = async (customer: User) => {
  emit('submit', { customerId: customer.id });
  isSwappingCustomer.value = false;
};

const viewAccount = () => {
  if (!props.initialInsuranceCase?.customer) return;
  router.push({
    name: 'user',
    params: { id: props.initialInsuranceCase.customer?.id },
  });
};

const initialValues = ref<CreateInsuranceCaseDto | UpdateInsuranceCaseDto>({
  startDate: props.initialInsuranceCase
    ? props.initialInsuranceCase?.startDate
    : initialStartDate.toJSDate(),
  endDate: props.initialInsuranceCase
    ? props.initialInsuranceCase?.endDate
    : initialStartDate.plus({ days: 2 }).toJSDate(),
  locationGeocodedAddress: props.initialInsuranceCase?.locationGeocodedAddress,
  customerData: {
    ...props.initialInsuranceCase?.customerData,
    firstName: props.initialInsuranceCase?.customerData?.firstName ?? '',
    lastName: props.initialInsuranceCase?.customerData?.lastName ?? '',
    street: props.initialInsuranceCase?.customerData?.street ?? '',
    zip: props.initialInsuranceCase?.customerData?.zip ?? '',
    city: props.initialInsuranceCase?.customerData?.city ?? '',
    country: props.initialInsuranceCase?.customerData?.country ?? '',
    preferredLocal: props.initialInsuranceCase?.customerData?.preferredLocal ?? Local.DE_DE,
  },
  insuranceProvider: props.initialInsuranceCase?.insuranceProvider,
  insuranceData: props.initialInsuranceCase?.insuranceData ?? undefined,
  additionalInfo: props.initialInsuranceCase?.additionalInfo ?? undefined,
});

const {
  handleSubmit,
  values: formValues,
  setFieldValue,
} = useForm<CreateInsuranceCaseDto | UpdateInsuranceCaseDto>({
  initialValues: initialValues.value,
  validationSchema: createInsuranceCaseSchema,
});

const handleSaveInsuranceCase = handleSubmit((values) => {
  emit('submit', values);
  initialValues.value = cloneDeep(values);
});

usePreventLeavingUnsavedForm(() => formHasChanged(initialValues.value, formValues));

const insuranceProviderOptions = useInsuranceProviderOptions();

const handleTimeRangeChange = (startDate: string, endDate: string) => {
  setFieldValue('startDate', new Date(startDate));
  setFieldValue('endDate', new Date(endDate));
};

const createBooking = async () => {
  if (!props.initialInsuranceCase) return;
  if (!props.initialInsuranceCase.startDate || !props.initialInsuranceCase.endDate) {
    return Alert.fire({
      html: t('dateMissingText'),
      icon: 'error',
      confirmButtonText: t('ok'),
    });
  }
  const startDate = DateTime.fromJSDate(new Date(props.initialInsuranceCase.startDate));
  if (!startDate.isValid) {
    return Alert.fire({
      html: t('invalidStartDateText'),
      icon: 'error',
      confirmButtonText: t('ok'),
    });
  }
  if (startDate < DateTime.now().minus({ days: 1 }).startOf('day')) {
    const { isConfirmed } = await Alert.fire({
      html: t('dateTooFarInPastText'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('continueAnyway'),
      cancelButtonText: t('cancel'),
    });
    if (!isConfirmed) return;
  }
  if (!props.initialInsuranceCase.locationGeocodedAddress) {
    return Alert.fire({
      html: t('locationMissingText'),
      icon: 'error',
      confirmButtonText: t('ok'),
    });
  }
  router.push({
    name: 'createBookingFromInsuranceCase',
    params: {
      insuranceCaseId: props.initialInsuranceCase.id,
    },
  });
};
</script>

<i18n lang="json">
{
  "en": {
    "pickupLocation": "Pickup location",
    "dropoffLocation": "Dropoff location",
    "timeAndLocation": "Time and location",
    "startDate": "Start date",
    "endDate": "End date",
    "email": "Email",
    "insuranceProvider": "Insurance provider",
    "customerData": "Customer data",
    "linkedCustomerAccount": "Linked customer account",
    "customerAccount": "Customer account",
    "noCustomerAccount": "No account linked",
    "linkAccount": "Link account",
    "viewAccount": "View account",
    "swapAccount": "Swap Account",
    "swap": "Swap",
    "add": "Add",
    "bookInsuranceCase": "Book insurance case",
    "viewLinkedBooking": "View linked booking",
    "locationMissingText": "<strong>Pickup</strong> and <strong>dropoff locations</strong> must be set to create a booking.",
    "dateMissingText": "<strong>Start</strong> and <strong>end date</strong> must be set to create a booking.",
    "invalidStartDateText": "<strong>Start date</strong> is invalid.",
    "dateTooFarInPastText": "<strong>Start date</strong> is more than one day in the past. Continue anyway?",
    "continueAnyway": "Continue anyway",
    "cancel": "Cancel",
    "status": "Status",
    "insuranceCaseStatus": {
      "INITIAL": "Initial",
      "BOOKED": "Booked",
      "HANDED_OVER": "Handed over",
      "RETURNED": "Returned",
      "CLOSED": "Closed"
    },
    "additionalInfo": "Additional Info"
  },
  "de": {
    "pickupLocation": "Abholort",
    "dropoffLocation": "Rückgabeort",
    "timeAndLocation": "Zeit und Ort",
    "startDate": "Startdatum",
    "endDate": "Enddatum",
    "email": "E-Mail",
    "insuranceProvider": "Versicherungsanbieter",
    "customerData": "Kundendaten",
    "linkedCustomerAccount": "Verknüpftes Kundenkonto",
    "customerAccount": "Kundenkonto",
    "noCustomerAccount": "Kein verknüpftes Konto",
    "linkAccount": "Konto verknüpfen",
    "viewAccount": "Konto ansehen",
    "swapAccount": "Konto austauschen",
    "swap": "Austauschen",
    "add": "Hinzufügen",
    "bookInsuranceCase": "Versicherungsfall buchen",
    "viewLinkedBooking": "Verknüpfte Buchung ansehen",
    "locationMissingText": "<strong>Abhol-</strong> und <strong>Rückgabeort</strong> müssen gesetzt sein um eine Buchung zu erstellen.",
    "dateMissingText": "<strong>Start-</strong> und <strong>Enddatum</strong> müssen gesetzt sein um eine Buchung zu erstellen.",
    "invalidStartDateText": "<strong>Startdatum</strong> ist ungültig.",
    "dateTooFarInPastText": "<strong>Startdatum</strong> liegt mehr als einen Tag in der Vergangenheit. Trotzdem fortfahren?",
    "continueAnyway": "Trotzdem fortfahren",
    "cancel": "Abbrechen",
    "status": "Status",
    "insuranceCaseStatus": {
      "INITIAL": "Initial",
      "BOOKED": "Gebucht",
      "HANDED_OVER": "Übergeben",
      "RETURNED": "Zurückgegeben",
      "CLOSED": "Abgeschlossen"
    },
    "additionalInfo": "Zusätzliche Infos"
  },
  "es": {
    "pickupLocation": "Lugar de recogida",
    "dropoffLocation": "Lugar de devolución",
    "timeAndLocation": "Fecha y lugar",
    "startDate": "Fecha de inicio",
    "endDate": "Fecha de fin",
    "email": "Correo electrónico",
    "insuranceProvider": "Aseguradora",
    "customerData": "Datos del cliente",
    "linkedCustomerAccount": "Cuenta de cliente vinculada",
    "customerAccount": "Cuenta de cliente",
    "noCustomerAccount": "Sin cuenta vinculada",
    "linkAccount": "Vincular cuenta",
    "viewAccount": "Ver cuenta",
    "swapAccount": "Cambiar cuenta",
    "swap": "Cambiar",
    "add": "Añadir",
    "bookInsuranceCase": "Reservar siniestro",
    "viewLinkedBooking": "Ver reserva vinculada",
    "locationMissingText": "Deben indicarse el <strong>lugar de recogida</strong> y el <strong>lugar de devolución</strong> para crear una reserva.",
    "dateMissingText": "Deben indicarse la <strong>fecha de inicio</strong> y la <strong>fecha de fin</strong> para crear una reserva.",
    "dateTooFarInPastText": "La <strong>fecha de inicio</strong> no puede ser anterior a más de un día.",
    "invalidStartDateText": "La <strong>fecha de inicio</strong> no es válida.",
    "continueAnyway": "Continuar de todos modos",
    "cancel": "Cancelar",
    "status": "Estado",
    "insuranceCaseStatus": {
      "INITIAL": "Inicial",
      "BOOKED": "Reservado",
      "HANDED_OVER": "Entregado",
      "RETURNED": "Devuelto",
      "CLOSED": "Cerrado"
    },
    "additionalInfo": "Información adicional"
  }
}
</i18n>
